import {
  WalletProviderPriority,
  getDefaultClient,
  getWalletProviderRegistry,
  hasExtension,
  registerExtension,
} from '@dynamic-labs-sdk/client/core';

import { registerEvmNetworkProviderBuilder } from '../../registerEvmNetworkProviderBuilder';
import { createWalletProviderFromEIP1193Provider } from '../../utils/createWalletProviderFromEIP1193Provider';
import { detectEvmWalletFromProvider } from '../utils/detectEvmWalletFromProvider';
import { getEvmInjectedProviders } from '../utils/getEvmInjectedProviders';

export const EVM_WINDOW_INJECTED_EXTENSION_KEY = 'evmWindowInjected';

/**
 * Adds the EVM window injected extension to the Dynamic client.
 *
 * This extension enables integration with wallets that are injected into window.ethereum
 * but don't support EIP-6963. The primary use case is **wallet app in-app browsers**
 * (e.g., MetaMask mobile browser, Trust Wallet browser, Coinbase Wallet browser),
 * which typically inject their provider into window.ethereum rather than announcing
 * via EIP-6963.
 *
 * Also useful as a fallback for:
 * - Older browser extension wallets that predate EIP-6963
 * - Safari or certain mobile browsers with limited EIP-6963 support
 *
 * Wallet providers registered via this extension have lower priority than EIP-6963
 * providers, so if a wallet supports both, the EIP-6963 version will be used.
 *
 * @param [client] - The Dynamic client instance. Only required when using multiple Dynamic clients.
  * @not-instrumented
 */
export const addEvmWindowInjectedExtension = (client = getDefaultClient()) => {
  if (
    hasExtension({ extensionKey: EVM_WINDOW_INJECTED_EXTENSION_KEY }, client)
  ) {
    return;
  }

  registerExtension({ extensionKey: EVM_WINDOW_INJECTED_EXTENSION_KEY }, client);
  registerEvmNetworkProviderBuilder(client);

  const walletProviderRegistry = getWalletProviderRegistry(client);
  const providers = getEvmInjectedProviders();

  providers.forEach((provider) => {
    const walletInfo = detectEvmWalletFromProvider({ provider });

    if (!walletInfo) {
      return;
    }

    const walletProvider = createWalletProviderFromEIP1193Provider(
      {
        displayName: walletInfo.displayName,
        eip1193: provider,
        icon: walletInfo.icon,
      },
      client
    );

    walletProviderRegistry.register({
      priority: WalletProviderPriority.WINDOW_INJECT,
      walletProvider,
    });
  });
};
